import { Inject } from '@angular/core';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, timeout } from 'rxjs';

export type RegionType = 'METROPOLITAN' | 'BASIC';
export type AnalysisStatus = 'READY' | 'PENDING' | 'UNAVAILABLE';
export type RegionDataSource = 'API' | 'FALLBACK';

export interface RegionNode {
    region_id: string;
    region_name: string;
    region_type: RegionType;
    parent_region_id: string | null;
    kosis_code: string;
    analysis_status: AnalysisStatus;
    available_years: number[];
    supported_metrics: string[];
    display_order: number;
    is_active: boolean;
    analysis_key?: string | null;
    source_table_id?: string;
    source_url?: string;
    code_reference_year?: number;
    mapping_validation?: string;
    data_availability_note?: string;
    children: RegionNode[];
}

export interface RegionTreeResponse {
    meta: Record<string, unknown>;
    regions: RegionNode[];
    sources?: Array<Record<string, unknown>>;
    validation_rules?: string[];
}

export interface RegionSelection {
    region_id: string;
    region_name: string;
    region_type: RegionType;
    parent_region_id: string | null;
    analysis_status: AnalysisStatus;
    analysis_key: string | null;
    source_table_id: string | null;
    kosis_code: string;
    data_source: RegionDataSource;
}

export interface RegionSelectionBlock {
    region_id: string;
    region_name: string;
    analysis_status: Exclude<AnalysisStatus, 'READY'>;
    parent_region_id: string | null;
    message: string;
}

@Injectable({ providedIn: 'root' })
export class RegionSelectionService {
    private readonly storageKey = 'reviewops-region-selection-v2';
    private readonly treeSubject = new BehaviorSubject<RegionTreeResponse | null>(null);
    private readonly selectionSubject = new BehaviorSubject<RegionSelection | null>(this.restoreSelection());
    private readonly dataSourceSubject = new BehaviorSubject<RegionDataSource | null>(null);
    private readonly blockedSubject = new BehaviorSubject<RegionSelectionBlock | null>(null);
    private loadPromise: Promise<RegionTreeResponse> | null = null;

    public readonly tree$ = this.treeSubject.asObservable();
    public readonly selection$ = this.selectionSubject.asObservable();
    public readonly dataSource$ = this.dataSourceSubject.asObservable();
    public readonly blocked$ = this.blockedSubject.asObservable();

    constructor(@Inject( HttpClient) private http: HttpClient) { }

    public get tree(): RegionTreeResponse | null {
        return this.treeSubject.value;
    }

    public get selection(): RegionSelection | null {
        return this.selectionSubject.value;
    }

    public get dataSource(): RegionDataSource | null {
        return this.dataSourceSubject.value;
    }

    public get blockedAttempt(): RegionSelectionBlock | null {
        return this.blockedSubject.value;
    }

    public get referenceYear(): number | null {
        const value = this.treeSubject.value?.meta?.['reference_year'];
        const year = Number(value);
        return Number.isInteger(year) ? year : null;
    }

    public async loadTree(): Promise<RegionTreeResponse> {
        if (this.treeSubject.value) return this.treeSubject.value;
        if (this.loadPromise) return this.loadPromise;

        this.loadPromise = this.fetchTree();
        try {
            return await this.loadPromise;
        } finally {
            this.loadPromise = null;
        }
    }

    public setTree(tree: RegionTreeResponse, dataSource?: RegionDataSource): void {
        if (!tree?.regions?.length) return;
        this.treeSubject.next(tree);
        if (dataSource) this.dataSourceSubject.next(dataSource);
        const current = this.selectionSubject.value;
        if (!current) return;
        const resolved = this.resolve(current.region_id);
        if (resolved?.analysis_status === 'READY') this.select(resolved);
        else this.clear();
    }

    public resolve(regionRef: string): RegionNode | null {
        const normalized = (regionRef || '').trim();
        if (!normalized || !this.treeSubject.value) return null;
        const nodes = this.flatten(this.treeSubject.value.regions);
        const direct = nodes.find(node =>
            node.region_id === normalized || node.analysis_key === normalized
        );
        if (direct) return direct;
        const named = nodes.filter(node => node.region_name === normalized);
        return named.length === 1 ? named[0] : null;
    }

    public select(node: RegionNode): RegionSelection | null {
        if (node.analysis_status !== 'READY') {
            this.block(node);
            return null;
        }
        const selection: RegionSelection = {
            region_id: node.region_id,
            region_name: node.region_name,
            region_type: node.region_type,
            parent_region_id: node.parent_region_id,
            analysis_status: node.analysis_status,
            analysis_key: node.analysis_key || null,
            source_table_id: node.source_table_id || null,
            kosis_code: node.kosis_code,
            data_source: this.dataSourceSubject.value || 'FALLBACK',
        };
        this.blockedSubject.next(null);
        this.selectionSubject.next(selection);
        window.sessionStorage.setItem(this.storageKey, JSON.stringify(selection));
        return selection;
    }

    public selectByRef(regionRef: string): RegionSelection | null {
        const node = this.resolve(regionRef);
        return node ? this.select(node) : null;
    }

    public block(node: RegionNode): RegionSelectionBlock | null {
        if (node.analysis_status === 'READY') return null;
        const message = node.analysis_status === 'PENDING'
            ? '해당 기초자치단체의 상세 LQ 데이터는 현재 검증·확장 중입니다. 현재는 상위 광역자치단체 단위 분석을 이용할 수 있습니다.'
            : '해당 지역은 현재 분석 데이터가 제공되지 않습니다.';
        const blocked: RegionSelectionBlock = {
            region_id: node.region_id,
            region_name: node.region_name,
            analysis_status: node.analysis_status,
            parent_region_id: node.parent_region_id,
            message,
        };
        this.blockedSubject.next(blocked);
        return blocked;
    }

    public clearBlocked(): void {
        this.blockedSubject.next(null);
    }

    public clear(): void {
        this.selectionSubject.next(null);
        window.sessionStorage.removeItem(this.storageKey);
    }

    public analysisReference(selection: RegionSelection | null = this.selection): string {
        if (!selection) return '';
        return selection.analysis_key || selection.region_id;
    }

    public statusLabel(status: AnalysisStatus | string): string {
        const labels: Record<string, string> = {
            READY: '분석 가능',
            PENDING: '데이터 확장 중',
            UNAVAILABLE: '데이터 미제공',
        };
        return labels[status] || '상태 확인 불가';
    }

    public dataSourceLabel(source: RegionDataSource | null = this.dataSource): string {
        return source === 'API'
            ? '실시간 API 데이터'
            : '정적 fallback 데이터(개발/장애 대응용)';
    }

    private async fetchTree(): Promise<RegionTreeResponse> {
        let tree: RegionTreeResponse;
        let source: RegionDataSource;
        try {
            const response: any = await firstValueFrom(
                this.http.get('/api/regions/tree', { headers: { Accept: 'application/json' } }).pipe(
                    timeout({ first: 5000 })
                )
            );
            tree = this.normalizeTree(response);
            source = 'API';
        } catch (error) {
            const fallback: any = await firstValueFrom(
                this.http.get<RegionTreeResponse>('/assets/data/region-master-2023.json').pipe(
                    timeout({ first: 5000 })
                )
            );
            tree = this.normalizeTree(fallback);
            source = 'FALLBACK';
        }
        this.setTree(tree, source);
        return tree;
    }

    private normalizeTree(payload: any): RegionTreeResponse {
        const tree = payload?.data?.regions ? payload.data : payload;
        if (!Array.isArray(tree?.regions) || tree.regions.length !== 17) {
            throw new Error('invalid region tree response');
        }
        return tree as RegionTreeResponse;
    }

    private flatten(regions: RegionNode[]): RegionNode[] {
        return regions.reduce((nodes: RegionNode[], region: RegionNode) => {
            nodes.push(region, ...(region.children || []));
            return nodes;
        }, []);
    }

    private restoreSelection(): RegionSelection | null {
        try {
            const stored = window.sessionStorage.getItem(this.storageKey);
            if (!stored) return null;
            const parsed = JSON.parse(stored);
            if (!parsed?.region_id || parsed.analysis_status !== 'READY') return null;
            return {
                ...parsed,
                data_source: parsed.data_source === 'API' ? 'API' : 'FALLBACK',
            } as RegionSelection;
        } catch (error) {
            return null;
        }
    }
}
